import { ChangeDetectionStrategy, Component, Input, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateService } from '@ngx-translate/core';

import { AddFormComponent } from '@shared/components/add-form/add-form.component';
import { 
  PosProfileFormConfig,
  getPosProfileFormConfig
} from '../../pos-profile.config';
import { PosProfileService, PosProfile } from '../../services/pos-profile.service';
import { PosReqDTO } from '@app/api/pos-service';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';

@Component({
  selector: 'app-pos-profile-form-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    AddFormComponent
  ],
  providers: [MessageService],
  templateUrl: './pos-profile-form-wrapper.component.html',
  styleUrls: ['./pos-profile-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PosProfileFormWrapperComponent implements OnInit {
  @Input() config!: PosProfileFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  
  // Form data management
  formData = signal<any>({});
  originalApiData = signal<any>(null); // Store original data from API
  
  // Loading state
  loading = signal(false);
  initialLoading = signal(true);
  
  // Dropdown data signals
  shopOptions = signal<Array<{ label: string; value: number }>>([]);
  shopTypeOptions = signal<Array<{ label: string; value: string }>>([]);

  // Computed property to combine all form data for initialData
  combinedInitialData = computed(() => {
    const baseData = {
      ...this.formData()
    };
    
    return baseData;
  });

  // Computed property to get form fields with translated default values
  translatedFormFields = computed(() => {
    if (!this.config?.formFields) return [];
    
    let fields = this.config.formFields.map(field => {
      let updatedField = { ...field };
      
      // Handle translated default values
      if (field.defaultValue && typeof field.defaultValue === 'string' && field.defaultValue.includes('.')) {
        const translatedValue = this.translateService.instant(field.defaultValue);
        if (translatedValue !== field.defaultValue) {
          updatedField = { ...updatedField, defaultValue: translatedValue };
        }
      }
      
      // Handle dropdown options
      if (
        field.controlName === 'channelShopId' &&
        (field.type === 'dropdown' || field.type === 'searchableDropdown')
      ) {
        updatedField = { ...updatedField, options: this.shopOptions() };
      }
      if (field.controlName === 'channelShopType' && field.type === 'dropdown') {
        updatedField = { ...updatedField, options: this.shopTypeOptions() };
      }

      // Disable deviceId and channelShopType in edit/view mode (only editable in create mode)
      if (field.controlName === 'deviceId' || field.controlName === 'channelShopType') {
        if (this.editMode || this.viewMode) {
          updatedField = { ...updatedField, disabled: true, readonly: true };
        }
      }

      return updatedField;
    });

    // Add activatedAt field when in view mode
    if (this.viewMode) {
      // Find position to insert field (after deviceId)
      const deviceIdIndex = fields.findIndex(f => f.controlName === 'deviceId');
      const insertIndex = deviceIdIndex >= 0 ? deviceIdIndex + 1 : fields.length;
      
      // Create activatedAt field
      const activatedAtField = {
        label: 'pos.form.fields.activated_at',
        controlName: 'activatedAt',
        type: 'text' as const,
        section: 'pos.form.sections.terminal_info',
        disabled: true,
        readonly: true,
      };
      
      // Insert field at desired position
      fields.splice(insertIndex, 0, activatedAtField);
    }

    return fields;
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private translateService: TranslateService,
    private posProfileService: PosProfileService
  ) {}

  ngOnInit(): void {
    // Initialize config with dynamic form fields if not already set
    if (!this.config) {
      this.config = getPosProfileFormConfig(this.posProfileService);
    }
    
    // Initialize shop options signal
    this.shopOptions.set([]);
    
    // Initialize shop type options signal
    this.shopTypeOptions.set([
      { label: "Offline", value: PosReqDTO.TypeEnum.Offline },
      { label: "Online", value: PosReqDTO.TypeEnum.Online },
    ]);
    
    // Load shop options for the form
    this.loadShopOptions();
    
    // Load existing data if in edit mode
    if (this.editMode || this.viewMode) {
      this.loadExistingData();
    } else {
      // For add mode, no data to load, so set loading to false
      this.initialLoading.set(false);
    }
  }

  /**
   * Load shop options for form dropdown
   */
  private loadShopOptions(): void {
    this.posProfileService.getPhysicalShopsForForm().subscribe({
      next: (options) => {
        // Update the shop options signal
        this.shopOptions.set(options);
      },
      error: (error) => {
        console.error('Error loading shop options:', error);
        this.shopOptions.set([]);
      }
    });
  }

  /**
   * Handle shop selection and populate related fields
   */
  onShopSelectionChange(shopId: number): void {
    if (shopId) {
      // Get shop details and populate channelShopCode and channelShopAddress
      this.posProfileService.getShopDetailsById(shopId).subscribe({
        next: (shopDetails) => {
          if (shopDetails) {
            // Update form data with shop information
            const currentFormData = this.formData();
            this.formData.set({
              ...currentFormData,
              channelShopId: shopId,
              channelShopCode: shopDetails.code,
              channelShopAddress: shopDetails.address,
              channelShopName: shopDetails.name
            });
          }
        },
        error: (error) => {
          console.error('Error getting shop details:', error);
        }
      });
    }
  }

  /**
   * Gate before API — matches required POS form rules (code + shop) without changing add-form.
   */
  private canSubmitPosProfileForm(data: any): boolean {
    const code = (data?.code ?? '').toString().trim();
    const shopId = data?.channelShopId;
    const shopOk =
      shopId !== null &&
      shopId !== undefined &&
      shopId !== '' &&
      Number(shopId) > 0;
    if (!code || !shopOk) {
      return false;
    }
    if (code.length > 50) {
      return false;
    }
    return /^[a-zA-Z0-9]+$/.test(code);
  }

  private loadExistingData(): void {
    const posId = this.route.snapshot.params['id'];
    
    if (posId) {
      this.posProfileService.getPosProfileById(Number(posId)).subscribe({
        next: (terminal : PosProfile | null) => {
          if (terminal) {
            this.loadFormDataFromApi(terminal);
          }
          
          // Small delay to ensure form component is ready
          setTimeout(() => {
            this.initialLoading.set(false);
          }, 100);
        },
        error: (error: any) => {
          console.error('Error loading POS profile terminal:', error);
          this.initialLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('pos.messages.load_failed')
          });
        }
      });
    } else {
      console.error('No POS ID found in route parameters');
    }
  }

  private loadFormDataFromApi(apiItem: any): void {
    // Store original API data for merging during save
    this.originalApiData.set(apiItem);
    console.log('Original API data stored:', apiItem);
    // Load form data
    const formDataToSet = {
      code: apiItem.code || '',
      channelShopId: apiItem.channelShopId || undefined,
      channelShopName: apiItem.channelShopName || '',
      channelShopCode: apiItem.channelShopCode || '',
      channelShopAddress: apiItem.channelShopAddress || '',
      channelShopType: apiItem.channelShopType,
      deviceId: apiItem.deviceId || '',
      description: apiItem.description || '',
      status: apiItem.status !== undefined ? apiItem.status : true,
      activatedAt: apiItem.activatedAt ? formatDateTime(apiItem.activatedAt) : '',
    };
    this.formData.set(formDataToSet);

    console.log('Loaded form data from API:', formDataToSet);
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'save') {
      if (!this.canSubmitPosProfileForm(event.data)) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant(
            'pos.messages.complete_required_fields',
          ),
        });
        return;
      }
      // Prevent multiple API calls if already saving
      if (this.loading()) {
        return;
      }
      // Set loading state immediately to prevent race conditions
      this.loading.set(true);
      this.savePosProfileData(event.data);
    } else if (event.action === 'cancel') {
      this.router.navigate([this.config.listRoute]);
    }
  }

  onFormValueChanges(value: any): void {
    const previousShopId = this.formData().channelShopId;
    const newShopId = value.channelShopId;

    this.formData.set(value);
    
    // Check if shop selection has changed and load shop details
    if (newShopId && newShopId !== previousShopId) {
      // Shop has changed, load shop details
      this.onShopSelectionChange(newShopId);
    }
  }

  private savePosProfileData(formData: any): void {
    try {
      const posId = this.route.snapshot.params['id'];
      
      if (this.editMode && posId) {
        // Get original API data to merge with form changes
        const originalData = this.originalApiData();
        
        // Update existing POS terminal - merge original data with form changes
        const posDTO: Partial<PosProfile> = {
          // Always include ID from route
          id: Number(posId),
          
          // Merge form data with original data (form data takes precedence)
          code: formData.code || originalData?.code || '',
          channelShopId: formData.channelShopId || originalData?.channelShopId || 0,
          channelShopName: formData.channelShopName || originalData?.channelShopName || '',
          channelShopCode: formData.channelShopCode || originalData?.channelShopCode || '',
          channelShopAddress: formData.channelShopAddress || originalData?.channelShopAddress || '',
          channelShopType: formData.channelShopType || originalData?.channelShopType,
          deviceId: formData.deviceId || originalData?.deviceId || '',
          description: formData.description || originalData?.description || '',
          status: formData.status !== undefined ? formData.status : (originalData?.status || true),
          
          // Include any other fields from original data that might not be in the form
          ...(originalData && {
            // Preserve any additional fields from the original API response
            createdBy: originalData.createdBy,
            creationDate: originalData.creationDate,
            lastUpdatedBy: originalData.lastUpdatedBy,
            lastUpdateDate: originalData.lastUpdateDate,
            deleteFlag: originalData.deleteFlag,
          })
        };
        
        this.posProfileService.updatePosProfile(Number(posId), posDTO).subscribe({
          next: (message) => {
            this.loading.set(false); // Reset loading state
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant('pos.messages.update_success')
            });
            
            // Navigate back to list
            setTimeout(() => this.router.navigate([this.config.listRoute]), 1000);
          },
          error: (error) => {
            this.loading.set(false); // Reset loading state
            console.error('Error updating POS profile:', error);
            const errorInfo = this.posProfileService.handleBackendValidationError(error, 'update');
            this.messageService.add(errorInfo);
          }
        });
      } else {
        // Create new POS terminal (for add mode)
        const posDTO: Partial<PosProfile> = {
          code: formData.code || '',
          channelShopId: formData.channelShopId || 0,
          channelShopName: formData.channelShopName || '',
          channelShopCode: formData.channelShopCode || '',
          channelShopAddress: formData.channelShopAddress || '',
          channelShopType: formData.channelShopType || PosReqDTO.TypeEnum.Online,
          deviceId: formData.deviceId || '',
          description: formData.description || '',
          status: formData.status !== undefined ? formData.status : true,
        };
        
        this.posProfileService.createPosProfile(posDTO).subscribe({
          next: (message) => {
            this.loading.set(false); // Reset loading state
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant('pos.messages.create_success')
            });
            
            // Navigate back to list
            setTimeout(() => this.router.navigate([this.config.listRoute]), 1000);
          },
          error: (error) => {
            this.loading.set(false); // Reset loading state
            console.error('Error creating POS profile:', error);
            const errorInfo = this.posProfileService.handleBackendValidationError(error, 'create');
            this.messageService.add(errorInfo);
          }
        });
      }
    } catch (error) {
      this.loading.set(false); // Reset loading state
      console.error('Error saving POS profile data:', error);
      this.messageService.add(this.posProfileService.handleBackendValidationError(error, 'create'));
    }
  }
}
